
import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PieChart } from "lucide-react";
import { currencyConverter } from "@/services/currencyConverter";
import { supabase } from "@/integrations/supabase/client";
import { ResizableSwappableCard } from "@/components/admin/dashboard/ResizableSwappableCard";
import { format, getMonth, getYear, startOfYear, endOfMonth, parseISO, isBefore, isWithinInterval } from "date-fns";
import { D3BarChart, D3ResponsiveContainer } from "../charts/D3ChartUtils";
import { useTheme } from "@/components/ThemeProvider";

interface ContractCostsData {
  name: string;
  monthlyCost: number;
  contractCount: number;
  month: string;
  [key: string]: any; // Allow dynamic month keys
}

interface MonthlyData {
  [month: string]: {
    [typeName: string]: {
      totalCost: number;
      count: number;
    }
  }
}

interface ContractCostsByTypeWidgetProps {
  rearrangingEnabled: boolean;
  onRemove: (id: string) => void;
  size: 'small' | 'medium' | 'large' | 'xlarge';
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
  defaultCurrencySymbol: string | null;
  defaultCurrencyId: string | null;
}

export function ContractCostsByTypeWidget({
  rearrangingEnabled,
  onRemove,
  size,
  onResize,
  defaultCurrencySymbol,
  defaultCurrencyId
}: ContractCostsByTypeWidgetProps) {
  const { toast } = useToast();
  const { theme } = useTheme();
  const [contractCostsByType, setContractCostsByType] = useState<ContractCostsData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const currentYear = getYear(new Date());
  
  const isDarkTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const textColor = isDarkTheme ? "#F1F1F1" : "#333333";
  
  const monthsThisYear = useMemo(() => {
    const now = new Date();
    const currentMonth = getMonth(now);
    const months = [];
    
    for (let i = 0; i <= currentMonth; i++) {
      const date = new Date(currentYear, i, 1);
      months.push(format(date, 'MMMM'));
    }
    
    return months;
  }, [currentYear]);

  useEffect(() => {
    let isMounted = true;
    
    async function fetchContractCosts() {
      if (!defaultCurrencyId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const { data: contracts, error } = await supabase
          .from('contracts')
          .select(`
            id,
            expected_cost,
            payment_frequency,
            currency_id,
            contract_type_id,
            contract_type:contract_types(name),
            signed_date,
            created_at
          `);

        if (error) throw error;
        if (!isMounted) return;

        const monthlyData: MonthlyData = {};
        monthsThisYear.forEach(month => {
          monthlyData[month] = {};
        });

        const startOfCurrentYear = startOfYear(new Date(currentYear, 0, 1));
        
        const contractsToProcess = [];
        
        for (const contract of contracts || []) {
          if (!contract.contract_type) continue;
          
          const typeName = contract.contract_type.name || 'Unknown';
          const expectedCost = contract.expected_cost || 0;
          const signedDate = contract.signed_date ? parseISO(contract.signed_date) : parseISO(contract.created_at);
          
          if (isBefore(signedDate, startOfCurrentYear)) continue;
          
          monthsThisYear.forEach((month, monthIndex) => {
            const monthStart = new Date(currentYear, monthIndex, 1);
            const monthEnd = endOfMonth(monthStart);
            
            if (isWithinInterval(signedDate, {
              start: monthStart,
              end: monthEnd
            })) {
              let monthlyCost = expectedCost;
              
              try {
                if (contract.payment_frequency) {
                  let paymentFrequency;
                  
                  if (typeof contract.payment_frequency === 'string') {
                    try {
                      paymentFrequency = JSON.parse(contract.payment_frequency);
                    } catch (e) {
                      paymentFrequency = null;
                    }
                  } else {
                    paymentFrequency = contract.payment_frequency;
                  }
                  
                  if (paymentFrequency && paymentFrequency.interval && paymentFrequency.unit) {
                    if (paymentFrequency.unit === 'Year') {
                      monthlyCost = expectedCost / (12 * paymentFrequency.interval);
                    } else if (paymentFrequency.unit === 'Month') {
                      monthlyCost = expectedCost / paymentFrequency.interval;
                    } else if (paymentFrequency.unit === 'Day') {
                      monthlyCost = (expectedCost * 30) / paymentFrequency.interval;
                    }
                  } else {
                    if (contract.payment_frequency === 'Monthly') {
                      monthlyCost = expectedCost;
                    } else if (contract.payment_frequency === 'Quarterly') {
                      monthlyCost = expectedCost / 3;
                    } else if (contract.payment_frequency === 'Semi-annually') {
                      monthlyCost = expectedCost / 6;
                    } else if (contract.payment_frequency === 'Annually') {
                      monthlyCost = expectedCost / 12;
                    }
                  }
                }
                
                if (contract.currency_id && contract.currency_id !== defaultCurrencyId) {
                  contractsToProcess.push({
                    month,
                    typeName,
                    monthlyCost,
                    currencyId: contract.currency_id
                  });
                } else {
                  if (!monthlyData[month][typeName]) {
                    monthlyData[month][typeName] = { totalCost: 0, count: 0 };
                  }
                  
                  monthlyData[month][typeName].totalCost += monthlyCost;
                  monthlyData[month][typeName].count += 1;
                }
              } catch (e) {
                console.error("Error processing contract cost:", e);
              }
            }
          });
        }
        
        for (const item of contractsToProcess) {
          try {
            const { convertedAmount } = await currencyConverter.convert(
              item.monthlyCost,
              item.currencyId,
              defaultCurrencyId
            );
            
            if (!monthlyData[item.month][item.typeName]) {
              monthlyData[item.month][item.typeName] = { totalCost: 0, count: 0 };
            }
            
            monthlyData[item.month][item.typeName].totalCost += convertedAmount;
            monthlyData[item.month][item.typeName].count += 1;
          } catch (conversionError) {
            console.error("Currency conversion error:", conversionError);
            
            if (!monthlyData[item.month][item.typeName]) {
              monthlyData[item.month][item.typeName] = { totalCost: 0, count: 0 };
            }
            
            monthlyData[item.month][item.typeName].totalCost += item.monthlyCost;
            monthlyData[item.month][item.typeName].count += 1;
          }
        }

        const formattedData: ContractCostsData[] = [];
        
        for (const month of monthsThisYear) {
          const monthData = monthlyData[month];
          
          Object.entries(monthData).forEach(([typeName, { totalCost, count }]) => {
            if (count > 0) {
              formattedData.push({
                name: typeName,
                monthlyCost: parseFloat(totalCost.toFixed(2)),
                contractCount: count,
                month
              });
            }
          });
        }
        
        formattedData.sort((a, b) => {
          const monthA = monthsThisYear.indexOf(a.month);
          const monthB = monthsThisYear.indexOf(b.month);
          
          if (monthA !== monthB) {
            return monthA - monthB;
          }
          
          return b.monthlyCost - a.monthlyCost;
        });
        
        if (isMounted) {
          setContractCostsByType(formattedData);
        }
      } catch (error) {
        console.error("Error fetching contract costs:", error);
        if (isMounted) {
          toast({
            title: "Error",
            description: "Failed to load contract costs data",
            variant: "destructive",
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchContractCosts();
    
    return () => {
      isMounted = false;
    };
  }, [defaultCurrencyId, toast, monthsThisYear, currentYear]);

  const chartData = useMemo(() => {
    const typeGroups: Record<string, number> = {};
    
    contractCostsByType.forEach(item => {
      if (!typeGroups[item.name]) {
        typeGroups[item.name] = 0;
      }
      typeGroups[item.name] += item.monthlyCost;
    });
    
    return Object.entries(typeGroups).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2))
    }));
  }, [contractCostsByType]);

  const formatCurrency = (value: number) => {
    return `${defaultCurrencySymbol || ''}${value.toLocaleString(undefined, { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 2 
    })}`;
  };
  
  // Make sure we're passing the correct widget ID to the ResizableSwappableCard
  const handleRemove = () => {
    if (onRemove) {
      onRemove("contract-costs-by-type");
    }
  };

  return (
    <ResizableSwappableCard
      id="contract-costs-by-type"
      size={size}
      showHandle={rearrangingEnabled}
      showResizeControl={rearrangingEnabled}
      onRemove={rearrangingEnabled ? handleRemove : undefined}
      onResize={rearrangingEnabled && onResize ? (newSize) => onResize("contract-costs-by-type", newSize) : undefined}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Monthly Contract Costs by Type ({currentYear})</CardTitle>
          <PieChart className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-52">
            <p className="text-muted-foreground">Loading data...</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-52">
            <p className="text-muted-foreground">No contract data available</p>
          </div>
        ) : (
          <ScrollArea className="h-[220px]">
            <div className="h-[220px] w-full">
              <D3ResponsiveContainer width="100%" height="100%">
                <D3BarChart
                  data={chartData}
                  color="#3b82f6"
                  valueFormatter={(value) => formatCurrency(value)}
                  margin={{ top: 20, right: 30, bottom: 70, left: 60 }}
                />
              </D3ResponsiveContainer>
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </ResizableSwappableCard>
  );
}
